import React from "react";
import { ReactComponent as PlayIcon } from "../assets/icon_play.svg";
import { ReactComponent as StopIcon } from "../assets/icon_stop.svg";
import DoneToggle from "./DoneToggle";
import TaskTitle from "./TaskTitle";
import LabelChip from "./LabelChip";
import Colors from "../constants/Colors";
import { getTitleDisplayMode } from "../utils/titleDisplaySetting";
import { formatDueDate } from "../utils/dueDate";
import { isShowLabelsOnTasks } from "../utils/showLabelsSetting";
import { isAiInvoiceEnabled } from "../utils/billingRateSetting";

const TaskItem = ({
  task,
  labels = [],
  onClick = () => {},
  onWipClick = () => {},
  onPlayClick = () => {},
  onStopClick = () => {},
  onAiPlayClick = () => {},
  onAiStopClick = () => {},
  onToggleDone = () => {},
}) => {
  let sideBarColor;
  let wipTextColor = "var(--text)"; // Default WIP text color

  switch (task.taskState) {
    case "task_state_done":
      sideBarColor = "#5FD788";
      break;
    case "task_state_wip":
      sideBarColor = Colors.primary;
      wipTextColor = Colors.primary;
      break;
    case "task_state_backlog":
      sideBarColor = "#E2E8F0"; // Light gray for backlog
      break;
    default:
      sideBarColor = "#E2E8F0"; // Default color (Light gray) if state is undefined
  }

  // Tracking indicator color, mirroring Android: human tracker = purple,
  // AI tracker = blue. Notes use the secondary accent.
  if (task.taskBeingTracked) {
    sideBarColor = "var(--track-human)";
  } else if (task.aiBeingTracked) {
    sideBarColor = "var(--track-ai)";
  } else if (task.usedAsNote) {
    sideBarColor = Colors.secondary;
  }

  // AI play/stop button shows when the global AI Invoice setting is on AND the
  // per-task "Track AI on this task" toggle is on (default). Mirrors Android's
  // showAiTracking = aiInvoiceEnabled && task.aiTrackingEnabled.
  const showAiTracking =
    !task.usedAsNote &&
    isAiInvoiceEnabled() &&
    task.aiTrackingEnabled !== false;

  const titleMode = getTitleDisplayMode();

  const showLabels = isShowLabelsOnTasks();
  const taskLabels =
    showLabels && task.labelIds && task.labelIds.length
      ? task.labelIds
          .map((id) => labels.find((l) => Number(l.id) === Number(id)))
          .filter(Boolean)
      : [];

  return (
    // Flex-row card: it stretches to the grid row height; the play/stop icon
    // and done circle stay vertically centered while the text column is
    // top-aligned (alignSelf on the content).
    <div onClick={onClick} style={styles.card}>
      <div style={{ ...styles.sideBar, backgroundColor: sideBarColor }}></div>
      <div style={{ width: "8px", flexShrink: 0 }}></div>

        {/* Tracking controls (not for Notes): human play/stop on top (purple),
            AI play/stop below (blue) — stacked vertically like Android. */}
        {!task.usedAsNote && (
          <div style={styles.iconContainer}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                task.taskBeingTracked ? onStopClick() : onPlayClick();
              }}
              style={styles.iconButton}
              aria-label={task.taskBeingTracked ? "Stop tracking" : "Start tracking"}
            >
              {task.taskBeingTracked ? (
                <StopIcon className="track-icon-human" style={styles.icon} />
              ) : (
                <PlayIcon className="track-icon-human" style={styles.icon} />
              )}
            </button>
            {showAiTracking && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  task.aiBeingTracked ? onAiStopClick() : onAiPlayClick();
                }}
                style={styles.iconButton}
                aria-label={task.aiBeingTracked ? "Stop AI tracking" : "Start AI tracking"}
              >
                {task.aiBeingTracked ? (
                  <StopIcon className="track-icon-ai" style={styles.icon} />
                ) : (
                  <PlayIcon className="track-icon-ai" style={styles.icon} />
                )}
              </button>
            )}
          </div>
        )}

        <div style={styles.content}>
          <TaskTitle text={task.title} mode={titleMode} style={styles.title} />
          {/* Show the description only when one exists — no "No Description"
              placeholder (matches Android). */}
          {task.description ? (
            <p style={styles.description}>{task.description}</p>
          ) : null}
          {/* Plain "Due Date: MMM dd, yyyy" label, hidden when unset (0) —
              Android TaskItem parity (no chip styling, no overdue color). */}
          {!task.usedAsNote && task.dueDate > 0 && (
            <p style={styles.dueDate}>{`Due Date: ${formatDueDate(task.dueDate)}`}</p>
          )}
          {!task.usedAsNote && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onWipClick();
              }}
              style={{ ...styles.wipButton, color: wipTextColor }}
            >
              WIP
            </button>
          )}
          {taskLabels.length > 0 && (
            <div style={styles.labelRow}>
              {taskLabels.map((label) => (
                <LabelChip key={label.id} name={label.name} color={label.color} />
              ))}
            </div>
          )}
        </div>

        {/* Mark-as-done control on the right (tasks only, not notes) */}
      {!task.usedAsNote && (
        <DoneToggle
          isDone={task.taskState === "task_state_done"}
          onToggle={onToggleDone}
        />
      )}
    </div>
  );
};

const styles = {
  card: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center", // play/stop + done circle centered in the full card
    width: "100%",
    minWidth: 0, // never let a long title expand this grid column
    height: "100%", // fill the (stretched) grid cell → uniform card heights
    padding: "16px",
    backgroundColor: "var(--card)",
    borderRadius: "8px",
    cursor: "pointer",
    position: "relative",
    boxSizing: "border-box",
  },
  sideBar: {
    width: "8px",
    position: "absolute",
    top: "0",
    bottom: "0",
    left: "0",
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
  },
  content: {
    flex: 1,
    minWidth: 0,
    alignSelf: "flex-start", // text stays at the top while controls center
    display: "flex",
    flexDirection: "column",
  },
  title: {
    margin: 0,
    color: "var(--text)",
    fontSize: "16px",
    fontWeight: "bold",
  },
  description: {
    margin: 0,
    marginTop: "8px",
    color: "var(--text-muted)",
    fontSize: "14px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    lineHeight: "1.5em",
    maxHeight: "4.5em",
  },
  dueDate: {
    margin: 0,
    marginTop: "8px",
    color: "var(--text-muted)",
    fontSize: "13px",
    whiteSpace: "nowrap",
  },
  wipButton: {
    background: "none",
    border: "none",
    padding: "0",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold",
    marginTop: "8px",
    textDecoration: "none",
    color: "var(--text)",
    textAlign: "left",
  },
  labelRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "10px",
  },
  iconContainer: {
    display: "flex",
    flexDirection: "column", // human on top, AI below
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    width: "24px",
    marginRight: "8px",
    flexShrink: 0,
  },
  iconButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  icon: {
    width: "20px",
    height: "20px",
  },
};

// Memoized so the boot-time burst of FirebaseContext updates (labels, rules,
// notifications, invoices, ...) doesn't re-render every card on the grid —
// that contention is what made the scrolling belt titles stutter for the
// first seconds after load. Cards only re-render when their own task object
// (a fresh reference on every tasks snapshot) or the labels list changes.
// Handler props are deliberately ignored: they're inline arrows recreated by
// the parent each render, but every one of them derives from the same `task`
// object — when the task changes, `task` changes, and the row re-renders with
// fresh handlers. Context functions they capture read live data via getDoc at
// call time, so a skipped render can't make them act on stale state.
export default React.memo(
  TaskItem,
  (prev, next) => prev.task === next.task && prev.labels === next.labels
);
